import React, {PropsWithChildren} from "react"

interface ConfirmProps {
    message: string
    title: string
    close?: (confirmed: boolean) => void
}

interface MessageProps {
    message: string
    type: 'error' | 'success' | 'info'
}

interface AlertContextValue {
    showConfirm: (params: ConfirmProps) => void
    showMessage: (params: MessageProps) => void
}

const AlertContext = React.createContext<AlertContextValue | undefined>(undefined)

export const useAlert = () => {
    const context = React.useContext(AlertContext)
    if (!context) {
        throw new Error('useConfirmation must be used within a ConfirmProvider')
    }
    return context
}

export const AlertProvider = (props: PropsWithChildren<{}>) => {
    const [confirm, setConfirm] = React.useState<{ show: boolean } & ConfirmProps>({show: false, message: "", title: ""})
    const [message, setMessage] = React.useState<{ show: boolean } & MessageProps>({show: false, message: "", type: 'success'})

    const showConfirm = (params: ConfirmProps) => {
        setConfirm({show: true, message: params.message, close: params.close, title: params.title})
    }

    const showMessage = (params: MessageProps) => {
        setMessage({show: true, message: params.message, type: params.type})
        setTimeout(() => setMessage({show: false, message: "", type: 'success'}), 20000)
    }

    const value: AlertContextValue = {showConfirm, showMessage}

    const handleClick = (confirmed: boolean) => {
        if (confirm.close) {
            confirm.close(confirmed)
        }
        setConfirm({show: false, message: "", title: ""})
    }

    return (
        <AlertContext.Provider value={value}>
            {props.children}
            {
                confirm.show &&
                <div className={`confirmation_modal`}>
                    <div className="modal_content">
                        <p className={'title'}>{confirm.title}</p>
                        <p className={'message'}>{confirm.message}</p>
                        <div className="modal_buttons">
                            <button className="no_button" onClick={() => handleClick(false)}>No</button>
                            <button className="yes_button" onClick={() => handleClick(true)}>Yes</button>
                        </div>
                    </div>
                </div>
            }

            {
                message.show &&
                <div className={`confirmation_modal`}>
                    <div className="modal_content">
                        <p className={'message'}>{message.message}</p>
                        <div className="modal_buttons">
                            <button className="yes_button" onClick={() => setMessage({...message, show: false})}>Close</button>
                        </div>
                    </div>
                </div>
            }
        </AlertContext.Provider>
    )
}